import { useState } from 'react'
import { useStore } from 'react-redux'
import { postCartItem, putCouponCode } from 'apis/cart'
import { algoliaEventsApi } from 'apis/algolia'
import { Actions as FlashMessageActions } from 'actions/shared/flashMessage'
import { AlgoliaConnectorProps } from './AlgoliaConnector'
import { addStoreLocaleByCookie } from 'lib/storeLocale/client'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
import { convertFromCentsToDollarsString } from 'components/FormatPrice/helpers'
import { parseSku } from 'lib/product'
import { Store } from 'redux'

type UseAddToCartResponseType = {
  handleClickAddToCart: (ev: React.SyntheticEvent<any>) => void
  handleClickAddToCartWithPromoCode: (ev: React.SyntheticEvent<any>) => void
  shouldBlockEvents: boolean
}

type UseAddToCartParams = AlgoliaConnectorProps & {
  isAddedToCart?: boolean
  sku: string
  artworkId: number
  price: number
  productEvent?: GA4CartEventObject
  customPromoCode?: string
  shouldSendAlgoliaEvents?: boolean
}

type AddToCartGAWhitelistParams = {
  artistName: string
  category: string
  price: number
  sku: string
  title: string
  listId?: string
  dataPosition: number
  artworkId: number
}

type GA4CartECommerceItem = {
  item_id: string
  item_name: string
  affiliation: string
  index: number
  item_brand: string
  item_art_id: string
  item_category: string
  item_variant: string
  item_list_id: string
  price: string
  quantity: number
}

type GA4CartECommerceObject = {
  currency: string
  value: string
  items: ReadonlyArray<GA4CartECommerceItem>
}

export type GA4CartEventObject = {
  event: string
  ecommerce: null | GA4CartECommerceObject
}

type AddToCartClickParams = {
  artworkId: number
  productEvent: GA4CartEventObject
  sku: string
  store: Store
  price: number
}

const PRODUCT_TYPES = {
  [`1`]: 'original',
  [`2`]: 'open',
  [`3`]: 'open',
  [`4`]: 'original',
}

export const addToCartGAWhitelist = ({
  artistName,
  category,
  price,
  sku,
  title,
  listId,
  dataPosition,
  artworkId,
}: AddToCartGAWhitelistParams): GA4CartEventObject => {
  // You can not add a PRODUCT with NO SKU so return empty object
  if (!sku) {
    return {
      event: '',
      ecommerce: null,
    }
  }

  const { productTypeId } = parseSku(sku) // [configurationId, userId, artworkId, productTypeId]

  return {
    event: `add_to_cart`,
    ecommerce: {
      currency: 'USD',
      value: convertFromCentsToDollarsString(price), // artwork price
      items: [
        {
          item_id: sku || '',
          item_name: title || '',
          affiliation: 'Online Store',
          index: dataPosition || 0,
          item_brand: artistName || '',
          item_art_id: `${artworkId}` || '',
          item_category: category || '',
          item_variant: PRODUCT_TYPES[productTypeId] || 'original',
          item_list_id: listId || '', // product list from where the product was added to cart
          price: convertFromCentsToDollarsString(price),
          quantity: 1,
        },
      ],
    },
  }
}

const useAddToCart = ({
  sku,
  searchIndice,
  algoliaUserToken,
  userId,
  artworkId,
  productEvent,
  price,
  customPromoCode,
  shouldSendAlgoliaEvents = true,
}: UseAddToCartParams): UseAddToCartResponseType => {
  const [shouldBlockEvents, setShouldBlockEvents] = useState(false) // Block UI

  const store = useStore()

  const addToCartClick = async ({
    artworkId,
    productEvent,
    sku,
    store,
    price,
  }: AddToCartClickParams): Promise<void> => {
    if (!sku) {
      return
    }

    const { message, success } = await postCartItem(sku)

    if (!success) {
      store.dispatch(
        FlashMessageActions.ADD({
          messages: [message],
          type: 'error',
        })
      )
      return
    }

    // Algolia event
    if (shouldSendAlgoliaEvents) {
      const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()
      algoliaEventsApi({
        events: [
          {
            eventName: 'add_to_cart',
            eventType: 'conversion',
            eventSubType: 'addToCart',
            index: searchIndice,
            userToken: algoliaUserToken,
            authenticatedUserToken: String(userId) || '',
            objectIDs: [String(artworkId)],
            objectData: [
              {
                price: convertFromCentsToDollarsString(price) || 0,
                quantity: 1,
              },
            ],
            currency: 'USD',
            ...(algoliaQueryIdFromLocalStorage && {
              queryID: algoliaQueryIdFromLocalStorage,
            }), // pass queryID only if its present, otherwise do not pass
          },
        ],
      })
    }

    const checkoutLocation = addStoreLocaleByCookie(`/cart`)

    // GTM/Datalayer is DISABLED
    if (!window.dataLayerEnabled) {
      window.location = checkoutLocation
      return
    }

    // Clear the previous ecommerce object
    window.dataLayer.push({ ecommerce: null })
    if (!!productEvent) {
      window.dataLayer.push({
        ...productEvent,

        eventCallback() {
          location.assign(checkoutLocation)
        },

        eventTimeout: 2000,
      })
    }
  }

  const addPromoAndItemToCart = async ({
    artworkId,
    productEvent,
    sku,
    store,
    price,
    customPromoCode,
  }): Promise<void> => {
    if (!sku || !customPromoCode) {
      return
    }

    const { success: promoSuccess } = await putCouponCode(customPromoCode)
    if (promoSuccess) {
      const { message, success } = await postCartItem(sku)
      if (!success) {
        store.dispatch(
          FlashMessageActions.ADD({
            messages: [message],
            type: 'error',
          })
        )
        return
      }
    } else {
      store.dispatch(
        FlashMessageActions.ADD({
          messages: ['Problem applying coupon code'],
          type: 'error',
        })
      )
      return
    }

    if (shouldSendAlgoliaEvents) {
      // Algolia event
      const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()
      algoliaEventsApi({
        events: [
          {
            eventName: 'add_to_cart',
            eventType: 'conversion',
            eventSubType: 'addToCart',
            index: searchIndice,
            userToken: algoliaUserToken,
            authenticatedUserToken: String(userId) || '',
            objectIDs: [String(artworkId)],
            objectData: [
              {
                price: convertFromCentsToDollarsString(price) || 0,
                quantity: 1,
              },
            ],
            currency: 'USD',
            ...(algoliaQueryIdFromLocalStorage && {
              queryID: algoliaQueryIdFromLocalStorage,
            }), // pass queryID only if its present, otherwise do not pass
          },
        ],
      })
    }

    const checkoutLocation = addStoreLocaleByCookie(`/cart`)

    // GTM/Datalayer is DISABLED
    if (!window.dataLayerEnabled) {
      window.location = checkoutLocation
      return
    }

    // Clear the previous ecommerce object
    window.dataLayer.push({ ecommerce: null })
    if (!!productEvent) {
      window.dataLayer.push({
        ...productEvent,

        eventCallback() {
          location.assign(checkoutLocation)
        },

        eventTimeout: 2000,
      })
    }
  }

  const handleClickAddToCart = (ev: React.SyntheticEvent<any>) => {
    ev.preventDefault()

    if (shouldBlockEvents) {
      return
    }

    addToCartClick({
      artworkId,
      productEvent,
      sku,
      store,
      price,
    })
  }

  const handleClickAddToCartWithPromoCode = (ev: React.SyntheticEvent<any>) => {
    ev.preventDefault()

    if (shouldBlockEvents) {
      return
    }

    addPromoAndItemToCart({
      artworkId,
      productEvent,
      sku,
      store,
      price,
      customPromoCode,
    })
  }

  return { handleClickAddToCart, handleClickAddToCartWithPromoCode, shouldBlockEvents }
}

export default useAddToCart
